import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { adminAuditListQuery } from '@xdocs/schemas';
import type { AdminAuditListResult, AuditEvent, ErrorAlert } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useWs } from '../../ws/WsProvider.js';

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'createdAt', dir: 'desc' };

export interface UseAdminUserActivityResult {
  rows: readonly AuditEvent[];
  total: number | null;
  nextCursor: string | null;
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
}

/**
 * One user's own actions (`admin.audit.list` with `actorUserId`) — the F54
 * user-detail "Activity" tab this task's own spec asks for, distinct from
 * the staff-facing unfiltered `/admin/audit` viewer another builder owns
 * (that page's file is out of this task's ownership; this hook only CALLS
 * the shared, already-frozen `admin.audit.list` op, same as F49's `/activity`
 * calls a differently-scoped list over the same `audit_events` store).
 * Excludes rows where `impersonatedUserId` is set on the SERVER side already
 * (§24: activity while impersonated attributes to the staff actor) — this
 * hook has no client-side filtering to do on top of that.
 */
export function useAdminUserActivity(userId: string): UseAdminUserActivityResult {
  const { wsClient, connectionState } = useWs();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected' && userId.length > 0;
  const queryKey = ['admin', 'users', 'activity', userId, query] as const;

  const result = useQuery<AdminAuditListResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'admin.audit.list', adminAuditListQuery.parse({ ...query, actorUserId: userId })),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const onRetry = useCallback(() => void result.refetch(), [result]);

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    rows,
    total,
    nextCursor: hasMore ? 'more' : null,
    query,
    onQueryChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry,
  };
}
